import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { InAppNotification } from '../../api/Types';
import { inAppNotificationService } from '../../services/InAppNotificationService';
import { shouldBackfillUnreadList } from './inAppNotifications.helpers';

interface UseInAppNotificationStateResult {
  notifications: InAppNotification[];
  notificationsLoading: boolean;
  unreadNotificationsCount: number;
  setNotifications: Dispatch<SetStateAction<InAppNotification[]>>;
  setUnreadNotificationsCount: Dispatch<SetStateAction<number>>;
  resetNotificationState: () => void;
  loadInAppNotifications: () => Promise<void>;
  handleNotificationClick: (notification: InAppNotification) => void;
}

const filterUnreadNotifications = (items: InAppNotification[]) =>
  items.filter((item) => !item.is_read);

export const useInAppNotificationState = (
  authorId?: number
): UseInAppNotificationStateResult => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  const resetNotificationState = useCallback(() => {
    setNotifications([]);
    setUnreadNotificationsCount(0);
  }, []);

  const loadInAppNotifications = useCallback(async () => {
    if (!authorId) {
      resetNotificationState();
      return;
    }

    setNotificationsLoading(true);
    try {
      const [items, unreadCount] = await Promise.all([
        inAppNotificationService.listMyNotifications(20),
        inAppNotificationService.getUnreadCount(),
      ]);
      setNotifications(filterUnreadNotifications(items));
      setUnreadNotificationsCount(unreadCount);
    } catch (error) {
      console.error('Failed to load in-app notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [authorId, resetNotificationState]);

  const markNotificationAsRead = useCallback(async (
    notification: InAppNotification,
    needsBackfill: boolean
  ) => {
    try {
      const didMarkAsRead = await inAppNotificationService.markAsRead(notification.id);
      if (!didMarkAsRead || needsBackfill) {
        await loadInAppNotifications();
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      await loadInAppNotifications();
    }
  }, [loadInAppNotifications]);

  const handleNotificationClick = useCallback((notification: InAppNotification) => {
    if (notification.is_read) {
      return;
    }

    const needsBackfill = shouldBackfillUnreadList(
      unreadNotificationsCount,
      notifications.length
    );

    setNotifications((previous) => previous.filter((item) => item.id !== notification.id));
    setUnreadNotificationsCount((previous) => Math.max(previous - 1, 0));

    void markNotificationAsRead(notification, needsBackfill);
  }, [markNotificationAsRead, notifications.length, unreadNotificationsCount]);

  return {
    notifications,
    notificationsLoading,
    unreadNotificationsCount,
    setNotifications,
    setUnreadNotificationsCount,
    resetNotificationState,
    loadInAppNotifications,
    handleNotificationClick,
  };
};

export default useInAppNotificationState;
